import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AUDIO_BASE, SCRIPT, timingFor, words, wordsBy } from "../data/voiceDemo";
import { LANGUAGE_COUNT } from "../data/voiceLanguages";
import { VoicePicker } from "./VoiceCallDemo";
import "./VoiceTeaser.css";

// Home page: a short clip from the voice call demo (Priya raises an objection, Aivio answers it) with live
// captions and the female/male voice switch, linking to the full call on the Sales and Voice Agent page.
const CLIP = [3, 4]; // line indexes in SCRIPT
const GAP_MS = 380;
const TEASER_LANGUAGES = ["English", "Hindi", "Gujarati", "Marathi", "Tamil", "Telugu", "Arabic", "Spanish"];
const OBJECTION = SCRIPT[CLIP[0]].objection;
const CUE = words(SCRIPT[CLIP[0]].text).findIndex((word) => word.startsWith(OBJECTION.cue));

function VoiceTeaser() {
  const [state, setState] = useState("idle"); // idle | playing | paused | done
  const [item, setItem] = useState(0); // position in CLIP
  const [spoken, setSpoken] = useState(0);
  const [progress, setProgress] = useState(0);
  const [agentVoice, setAgentVoice] = useState("female");
  const [captionsOnly, setCaptionsOnly] = useState(false);
  const player = useRef(null);

  const timings = CLIP.map((index) => timingFor(index, agentVoice));
  const total = timings.reduce((sum, timing) => sum + (timing ? timing.duration : 0), 0) || 1;

  useEffect(() => {
    if (state !== "playing") return undefined;
    const timing = timings[item];
    let cancelled = false, frame = 0, timer = 0, began = 0;
    const before = timings.slice(0, item).reduce((sum, t) => sum + t.duration, 0);
    const update = (seconds) => {
      setSpoken(wordsBy(timing, seconds));
      setProgress(Math.min(1, (before + seconds) / total));
    };
    const finish = () => {
      if (cancelled) return;
      setSpoken(timing.words.length);
      timer = window.setTimeout(() => {
        if (cancelled) return;
        if (item + 1 < CLIP.length) { setItem(item + 1); setSpoken(0); } else { setState("done"); setProgress(1); }
      }, GAP_MS);
    };
    const audio = player.current;
    if (audio && !captionsOnly) {
      if (!audio.src.endsWith(timing.file)) audio.src = AUDIO_BASE + timing.file;
      const follow = () => { if (!cancelled) { update(audio.currentTime); frame = window.requestAnimationFrame(follow); } };
      audio.onended = finish;
      audio.play().then(() => { frame = window.requestAnimationFrame(follow); }).catch((error) => {
        if (!cancelled && error?.name !== "AbortError") setCaptionsOnly(true);
      });
    } else {
      // No audio: pace the captions like the recording.
      began = performance.now() - (audio && audio.src.endsWith(timing.file) ? audio.currentTime * 1000 : 0);
      const tick = () => {
        if (cancelled) return;
        const seconds = (performance.now() - began) / 1000;
        update(seconds);
        if (seconds >= timing.duration) finish(); else frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    }
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      if (audio) { audio.onended = null; audio.pause(); }
    };
  }, [state, item, agentVoice, captionsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => player.current?.pause(), []);

  const toggle = () => {
    // Create and start the player inside the click so strict autoplay rules allow the second line.
    if (!player.current && typeof Audio !== "undefined") player.current = new Audio();
    if (state === "playing") { setState("paused"); return; }
    if (state === "idle" || state === "done") {
      setItem(0); setSpoken(0); setProgress(0);
      if (player.current && timings[0]) { player.current.src = AUDIO_BASE + timings[0].file; player.current.play().catch(() => {}); }
    } else if (player.current && !captionsOnly) {
      player.current.play().catch(() => {});
    }
    setState("playing");
  };

  const started = state !== "idle";
  const raised = started && (item > 0 || spoken > CUE);
  const handled = state === "done";

  return (
    <section className="voice-teaser-section" data-reveal>
      <div className="container voice-teaser-inner">
        <div className="voice-teaser-copy">
          <div className="eyebrow section-eyebrow">Voice agents · listen</div>
          <h2>Hear Aivio handle a hesitant buyer. <span>In a voice that sounds human.</span></h2>
          <p>Aivio answers calls, listens for concerns and responds with approved information, while it fills in your CRM. Press play to hear a moment from a sales call, then pick the voice that suits your brand.</p>
          <div className="voice-teaser-langs">
            <small>Speaks {LANGUAGE_COUNT}+ languages</small>
            <div>{TEASER_LANGUAGES.map((language) => <span key={language}>{language}</span>)}<Link to="/use-cases/sales-voice#languages">+{LANGUAGE_COUNT - TEASER_LANGUAGES.length} more</Link></div>
          </div>
          <Link to="/use-cases/sales-voice#try-aivio" className="voice-teaser-link">Hear the full call and watch the CRM fill in <span aria-hidden="true">→</span></Link>
        </div>

        <div className={`voice-teaser-card is-${state}`}>
          <div className="voice-teaser-top">
            <button type="button" className="voice-teaser-play" onClick={toggle} aria-label={state === "playing" ? "Pause" : state === "done" ? "Play again" : "Play the clip"}>
              {state === "playing"
                ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>
                : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>}
            </button>
            <div className="voice-teaser-meta">
              <strong>Sales call · objection handling</strong>
              <small>{state === "done" ? "Play again" : state === "playing" ? (item === 0 ? "Priya is speaking" : "Aivio is speaking") : "Priya Sharma, Northwind Logistics"} · {Math.round(total)}s</small>
            </div>
            <div className="voice-teaser-bars" aria-hidden="true">{Array.from({ length: 5 }, (_, index) => <i key={index} style={{ "--d": `${index * 120}ms` }} />)}</div>
          </div>
          <div className="voice-teaser-progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>

          <div className="voice-teaser-lines" aria-live="polite">
            {CLIP.map((index, position) => {
              const line = SCRIPT[index];
              const list = words(line.text);
              const lit = !started ? 0 : position < item || state === "done" ? list.length : position === item ? spoken : 0;
              return (
                <div className={`voice-teaser-line is-${line.who}${started && position === item && state !== "done" ? " is-current" : ""}`} key={index}>
                  <b>{line.who === "agent" ? "Aivio" : "Priya"}</b>
                  <p>{list.map((word, i) => <span key={i} className={i < lit ? "is-said" : ""}>{word} </span>)}</p>
                </div>
              );
            })}
          </div>

          <div className={`voice-teaser-flag${raised ? " is-shown" : ""}${handled ? " is-handled" : ""}`}>
            <span>{handled ? "✓ Objection handled" : "Objection detected"}</span>
            {handled ? SCRIPT[CLIP[1]].resolves[OBJECTION.key] : OBJECTION.label}
          </div>

          <div className="voice-teaser-foot">
            <VoicePicker value={agentVoice} onChange={setAgentVoice} />
            {captionsOnly && <small>Captions only: audio couldn’t play here</small>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default VoiceTeaser;
